import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  Button,
  Alert,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  LinearProgress,
  TextField,
} from '@mui/material';
import {
  Schedule,
  CheckCircle,
  Error,
  Refresh,
  RestartAlt,
  Assignment,
  Cancel,
  Close,
  SystemUpdate,
  Send,
} from '@mui/icons-material';
import { Task } from '../types';
import { taskApi } from '../api';
import { formatBytes, formatDateTime, formatRelativeTime, useI18n } from '../i18n';

interface TaskListProps {
  deviceId: string;
}

const QUICK_COMMANDS = ['help', 'info', 'ls /', 'modbus discover'];

function formatTaskResult(result: unknown): string {
  if (result == null || result === '') {
    return '';
  }
  if (typeof result === 'string') {
    return result;
  }
  if (typeof result === 'object') {
    const obj = result as Record<string, unknown>;
    if (typeof obj.message === 'string') {
      return obj.message;
    }
    if (typeof obj.status === 'string' && obj.percentage === undefined) {
      return obj.status;
    }
    return JSON.stringify(result, null, 2);
  }
  return String(result);
}

const TaskList: React.FC<TaskListProps> = ({ deviceId }) => {
  const { t } = useI18n();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [restarting, setRestarting] = useState(false);
  const [command, setCommand] = useState('');
  const [sending, setSending] = useState(false);

  const loadTasks = async () => {
    try {
      setError(null);
      const taskList = await taskApi.getTasks(deviceId);
      setTasks(taskList);
    } catch (err) {
      setError(t('tasks.loadFailed'));
      console.error('Error loading tasks:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTasks();
    const interval = setInterval(loadTasks, 5000);
    return () => clearInterval(interval);
  }, [deviceId]);

  const handleSendCommand = async (raw?: string) => {
    const cmd = (raw ?? command).trim();
    if (!cmd || sending) {
      return;
    }
    try {
      setSending(true);
      setError(null);
      await taskApi.createTask(deviceId, { command: cmd });
      setCommand('');
      await loadTasks();
    } catch (err) {
      setError(t('tasks.queueFailed'));
      console.error('Error creating command task:', err);
    } finally {
      setSending(false);
    }
  };

  const handleRestart = async () => {
    try {
      setRestarting(true);
      setError(null);
      await taskApi.createTask(deviceId, { command: 'restart' });
      await loadTasks();
    } catch (err) {
      setError(t('tasks.restartFailed'));
      console.error('Error creating restart task:', err);
    } finally {
      setRestarting(false);
    }
  };

  const handleCancelTask = async (taskId: string) => {
    try {
      setError(null);
      await taskApi.cancelTask(deviceId, taskId);
      await loadTasks();
    } catch (err) {
      setError(t('tasks.cancelFailed'));
      console.error('Error cancelling task:', err);
    }
  };

  const isOTATask = (task: Task): boolean => {
    return task.command.toLowerCase().includes('update') ||
           task.command.toLowerCase().includes('ota') ||
           task.command.toLowerCase().includes('.bin');
  };

  const getTaskIcon = (task: Task) => {
    const isOTA = isOTATask(task);

    switch (task.state) {
      case 'pending':
        return isOTA ? <SystemUpdate color="warning" /> : <Schedule color="warning" />;
      case 'running':
        return <CircularProgress size={20} />;
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Error color="error" />;
      case 'cancelled':
        return <Cancel color="disabled" />;
      default:
        return isOTA ? <SystemUpdate /> : <Assignment />;
    }
  };

  const getTaskColor = (state: Task['state']) => {
    switch (state) {
      case 'pending':
        return 'warning';
      case 'running':
        return 'info';
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return formatDateTime(timestamp);
  };

  const getOTAProgress = (task: Task): { percentage: number; status?: string; currentSize?: number; totalSize?: number } | null => {
    if (!isOTATask(task) || !task.result) return null;

    if (typeof task.result === 'object' && task.result.percentage !== undefined) {
      return {
        percentage: task.result.percentage || 0,
        status: task.result.status,
        currentSize: task.result.currentSize,
        totalSize: task.result.totalSize
      };
    }

    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="center" p={3}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {t('tasks.loading')}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Assignment color="primary" />
            {t('tasks.title')}
          </Typography>
          <Box display="flex" gap={1}>
            <Tooltip title={t('common.refresh')}>
              <IconButton onClick={loadTasks} size="small">
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              color="warning"
              startIcon={restarting ? <CircularProgress size={16} /> : <RestartAlt />}
              onClick={handleRestart}
              disabled={restarting || sending}
              size="small"
            >
              {restarting ? t('tasks.restarting') : t('tasks.restart')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSendCommand();
          }}
          sx={{ display: 'flex', gap: 1, mb: 1 }}
        >
          <TextField
            size="small"
            fullWidth
            placeholder={t('tasks.placeholder')}
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            disabled={sending}
            autoComplete="off"
            inputProps={{ spellCheck: false }}
          />
          <Button
            type="submit"
            variant="contained"
            startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <Send />}
            disabled={sending || !command.trim()}
          >
            {t('common.send')}
          </Button>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          {QUICK_COMMANDS.map((cmd) => (
            <Chip
              key={cmd}
              label={cmd}
              size="small"
              variant="outlined"
              onClick={() => handleSendCommand(cmd)}
              disabled={sending}
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('tasks.hint')}
        </Typography>

        {tasks.length === 0 ? (
          <Box textAlign="center" py={3}>
            <Typography variant="body2" color="text.secondary">
              {t('tasks.empty')}
            </Typography>
          </Box>
        ) : (
          <List dense>
            {tasks.map((task, index) => {
              const otaProgress = getOTAProgress(task);
              const resultText = otaProgress ? '' : formatTaskResult(task.result);
              return (
              <React.Fragment key={task.id}>
                <ListItem sx={{ px: 0, alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ mt: 0.5 }}>
                    {getTaskIcon(task)}
                  </ListItemIcon>
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="medium" sx={{ fontFamily: 'monospace' }}>
                          {task.command}
                        </Typography>
                        <Chip
                          label={t(`tasks.state.${task.state}`)}
                          size="small"
                          color={getTaskColor(task.state) as any}
                        />
                        {(task.state === 'pending' || task.state === 'running') && (
                          <Tooltip title={t('tasks.cancel')}>
                            <IconButton
                              size="small"
                              onClick={() => handleCancelTask(task.id)}
                              sx={{ ml: 'auto' }}
                            >
                              <Close fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          {t('tasks.created', { time: formatTimestamp(task.createdAt), relative: formatRelativeTime(task.createdAt) })}
                        </Typography>
                        {task.updatedAt !== task.createdAt && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {t('tasks.updated', { time: formatTimestamp(task.updatedAt), relative: formatRelativeTime(task.updatedAt) })}
                          </Typography>
                        )}

                        {task.state === 'pending' && (
                          <Typography variant="caption" color="warning.main" display="block">
                            {t('tasks.waitingPoll')}
                          </Typography>
                        )}
                        {task.state === 'running' && !otaProgress && (
                          <Typography variant="caption" color="info.main" display="block">
                            {t('tasks.executing')}
                          </Typography>
                        )}

                        {otaProgress && (task.state === 'running' || (task.state === 'failed' && otaProgress.percentage > 0)) && (
                          <Box mt={1} mb={1}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                              <Typography variant="caption" color="text.secondary">
                                {otaProgress.status || t('tasks.otaUpdate')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {otaProgress.percentage}%
                                {otaProgress.currentSize && otaProgress.totalSize &&
                                  ` (${formatBytes(otaProgress.currentSize)}/${formatBytes(otaProgress.totalSize)})`
                                }
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={otaProgress.percentage}
                              color={task.state === 'failed' ? 'error' : 'primary'}
                              sx={{ height: 6, borderRadius: 3 }}
                            />
                          </Box>
                        )}

                        {resultText && (
                          <Box
                            component="pre"
                            sx={{
                              mt: 1,
                              mb: 0,
                              p: 1.5,
                              bgcolor: task.state === 'failed' ? '#3b1515' : '#1e1e1e',
                              color: task.state === 'failed' ? '#ffcdd2' : '#e0e0e0',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                              fontSize: 12,
                              lineHeight: 1.45,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                              borderRadius: 1,
                              maxHeight: 320,
                              overflow: 'auto',
                            }}
                          >
                            {resultText}
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
                {index < tasks.length - 1 && <Divider />}
              </React.Fragment>
              );
            })}
          </List>
        )}

        <Box mt={2} pt={2} borderTop="1px solid" borderColor="divider">
          <Typography variant="caption" color="text.secondary">
            {t('tasks.refreshHint')}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TaskList;
